import React, { useState, useEffect } from 'react';
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';
import ClientHeader from '../../Common/Layout/ClientHeader';
import { rexTechTheme } from '../../Common/Theme';
import { 
  Building,
  Briefcase,
  User,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

const UserBranchList = () => {
    const [branch, setBranch] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userId, setUserId] = useState(null);

    useEffect(() => {
        // Get userId from localStorage
        const storedUserId = localStorage.getItem('userId');
        if (storedUserId) {
            setUserId(storedUserId);
            fetchUserBranch(storedUserId);
        } else {
            setError('User ID not found in localStorage');
            setIsLoading(false);
        }
    }, []);

    const fetchUserBranch = async (userId) => {
        try {
            setIsLoading(true);
            setError(null);
            
            // Call the new API endpoint
            const response = await axios.get(`${config.APIACTIVEURL}branch/user/${userId}`);
            
            if (response.data) {
                setBranch(response.data);
            } else {
                setError('No branch found for this user');
            }
        } catch (error) {
            console.error("Error fetching user branch", error);
            if (error.response?.status === 404) {
                setError('No branch found for this user');
            } else {
                setError('Failed to load branch information');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getBranchTypeBadge = (type) => {
        if (!type) return null;
        
        const typeLower = type.toLowerCase();
        const isMain = typeLower === 'main';
        
        return (
            <span style={{
                padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.md}`,
                backgroundColor: isMain ? rexTechTheme.colors.primary.lightest : rexTechTheme.colors.neutral.lightGray,
                color: isMain ? rexTechTheme.colors.primary.dark : rexTechTheme.colors.neutral.darkGray,
                borderRadius: rexTechTheme.borderRadius.small,
                fontSize: rexTechTheme.typography.fontSize.xs,
                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
            }}>
                {type}
            </span>
        );
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: rexTechTheme.colors.neutral.lightGray,
            fontFamily: rexTechTheme.typography.fontFamily
        }}>
            <ClientHeader />
            
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                padding: rexTechTheme.spacing.xl
            }}>
                {/* Header */}
                <div style={{
                    marginBottom: rexTechTheme.spacing.xl
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: rexTechTheme.spacing.lg
                    }}>
                        <div>
                            <h1 style={{
                                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.bold,
                                marginBottom: rexTechTheme.spacing.xs
                            }}>
                                My Branches
                            </h1>
                            <p style={{
                                color: rexTechTheme.colors.neutral.darkGray
                            }}>
                                Branch information associated with your account
                            </p>
                        </div>
                        
                        <button
                            onClick={() => fetchUserBranch(userId)}
                            style={{
                                padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                backgroundColor: rexTechTheme.colors.primary.main,
                                color: rexTechTheme.colors.neutral.white,
                                border: 'none',
                                borderRadius: rexTechTheme.borderRadius.medium,
                                fontSize: rexTechTheme.typography.fontSize.regular,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm,
                                ':hover': {
                                    backgroundColor: rexTechTheme.colors.primary.dark
                                }
                            }}
                        >
                            <RefreshCw size={18} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div style={{
                    backgroundColor: rexTechTheme.colors.neutral.white,
                    borderRadius: rexTechTheme.borderRadius.large,
                    boxShadow: rexTechTheme.shadows.light,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    overflow: 'hidden'
                }}>
                    {isLoading ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: rexTechTheme.spacing.xxl,
                            color: rexTechTheme.colors.primary.main
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                border: `3px solid ${rexTechTheme.colors.primary.lightest}`,
                                borderTop: `3px solid ${rexTechTheme.colors.primary.main}`,
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                marginBottom: rexTechTheme.spacing.md
                            }} />
                            <style>{`
                                @keyframes spin {
                                    0% { transform: rotate(0deg); }
                                    100% { transform: rotate(360deg); }
                                }
                            `}</style>
                            <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>
                                Loading your branch information...
                            </span>
                        </div>
                    ) : error ? (
                        <div style={{
                            textAlign: 'center',
                            padding: rexTechTheme.spacing.xxl,
                            color: rexTechTheme.colors.neutral.darkGray
                        }}>
                            <AlertCircle size={48} style={{ 
                                marginBottom: rexTechTheme.spacing.md,
                                color: rexTechTheme.colors.accent.orange,
                                opacity: 0.5
                            }} />
                            <h3 style={{ 
                                color: rexTechTheme.colors.accent.orange,
                                marginBottom: rexTechTheme.spacing.sm
                            }}>
                                {error}
                            </h3>
                            <p style={{ marginBottom: rexTechTheme.spacing.lg }}>
                                Please check if you're properly logged in and have an associated branch
                            </p>
                        </div>
                    ) : branch ? (
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            minWidth: '800px'
                        }}>
                            <thead>
                                <tr style={{
                                    backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                    borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`
                                }}>
                                    <th style={{
                                        padding: rexTechTheme.spacing.lg,
                                        textAlign: 'left',
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Branch
                                    </th>
                                    <th style={{
                                        padding: rexTechTheme.spacing.lg,
                                        textAlign: 'left',
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Client
                                    </th>
                                    <th style={{
                                        padding: rexTechTheme.spacing.lg,
                                        textAlign: 'left',
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        width: '120px'
                                    }}>
                                        Type
                                    </th>
                                 
                                </tr>
                            </thead>
                            <tbody>
                                <tr style={{
                                    backgroundColor: rexTechTheme.colors.primary.lightest,
                                    borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                    transition: 'all 0.2s ease'
                                }}>
                                    <td style={{ padding: rexTechTheme.spacing.lg }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: rexTechTheme.spacing.md
                                        }}>
                                            <div style={{
                                                width: '40px',
                                                height: '40px',
                                                backgroundColor: rexTechTheme.colors.primary.main,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Building size={18} color={rexTechTheme.colors.neutral.white} />
                                            </div>
                                            <div>
                                                <div style={{
                                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                    color: rexTechTheme.colors.primary.dark,
                                                    fontSize: rexTechTheme.typography.fontSize.regular
                                                }}>
                                                    {branch.branchName}
                                                </div>
                                               
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ padding: rexTechTheme.spacing.lg }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: rexTechTheme.spacing.md
                                        }}>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                backgroundColor: rexTechTheme.colors.primary.lightest,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Briefcase size={14} color={rexTechTheme.colors.primary.main} />
                                            </div>
                                            <div>
                                                <span style={{
                                                    color: rexTechTheme.colors.neutral.darkGray
                                                }}>
                                                    {branch.companyName}
                                                </span>
                                              
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ padding: rexTechTheme.spacing.lg }}>
                                        {getBranchTypeBadge(branch.branchType)}
                                    </td>
                                   
                                </tr>
                            </tbody>
                        </table>
                    ) : null}
                </div>

                {/* User Info Footer */}
                <div style={{
                    marginTop: rexTechTheme.spacing.lg,
                    textAlign: 'center',
                    color: rexTechTheme.colors.neutral.darkGray,
                    fontSize: rexTechTheme.typography.fontSize.small
                }}>
                    <p>
                        This branch information is retrieved based on your user ID stored in localStorage
                    </p>
                </div>
            </div>
        </div>
    );
};

export default UserBranchList;